import { FlagIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FormattedMessage, useIntl } from "react-intl";
import { Dialog, DialogPanel } from "@headlessui/react";
import { FormEvent, useState } from "react";
import Select from "../common/forms/select";
import TextArea from "../common/forms/textarea";
import Button from "../common/buttons/button";
import { useForm } from "@inertiajs/react";
import AlertWithAccentBorder from "../common/containers/alertWithAccentBorder";
import Label from "../common/forms/label";
import Field from "../common/forms/field";
import Error from "../common/forms/error";

export default function ReportOpportunity({ slug }: { slug: string }) {
    const [open, setOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const intl = useIntl();
    const { data, setData, post, errors } = useForm({
        reason: "notset",
        feedback: "",
    });

    const submit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        post(route("inertia.opportunity.report", slug), {
            onSuccess: () => setSubmitted(true),
        });
    };
    return (
        <div>
            <div className="flex justify-center">
                <button
                    onClick={() => setOpen(true)}
                    className="cursor-pointer flex items-center text-sm text-gray-700"
                >
                    <span className="underline underline-offset-4">
                        <FormattedMessage id="Report this opportunity" />
                    </span>
                </button>
            </div>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                className="relative z-999"
            >
                <div
                    className="fixed inset-0 bg-gray-500/75"
                    aria-hidden="true"
                />
                <div className="fixed inset-0 flex w-screen items-center justify-center p-8">
                    <DialogPanel className="w-full sm:max-w-lg rounded-sm bg-white p-8">
                        <div className="flex items-center justify-between pb-6">
                            <h2 className="flex items-center text-xl font-medium">
                                <FlagIcon className="size-5 mr-2" />
                                <FormattedMessage id="Report this opportunity" />
                            </h2>
                            <button
                                onClick={() => setOpen(false)}
                                type="button"
                                className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                            >
                                <span className="sr-only">
                                    <FormattedMessage id="Close" />
                                </span>
                                <XMarkIcon className="size-6" />
                            </button>
                        </div>
                        <form onSubmit={submit}>
                            <Field>
                                <Label className="sr-only">
                                    <FormattedMessage id="Reason" />
                                </Label>
                                <Select
                                    className="w-full"
                                    value={data.reason}
                                    onChange={(e) =>
                                        setData("reason", e.target.value)
                                    }
                                >
                                    <option value="notset">
                                        <FormattedMessage id="Select a reason" />
                                    </option>
                                    <option
                                        value={intl.formatMessage({
                                            id: "I don't feel this opportunity is appropriate for Voice",
                                        })}
                                    >
                                        <FormattedMessage id="I don't feel this opportunity is appropriate for Voice" />
                                    </option>
                                    <option
                                        value={intl.formatMessage({
                                            id: "I feel this opportunity uses offensive language",
                                        })}
                                    >
                                        <FormattedMessage id="I feel this opportunity uses offensive language" />
                                    </option>
                                    <option
                                        value={intl.formatMessage({
                                            id: "Other",
                                        })}
                                    >
                                        <FormattedMessage id="Other" />
                                    </option>
                                </Select>
                                <Error error={errors.reason} />
                            </Field>
                            <Field className="mt-5">
                                <Label className="sr-only">
                                    <FormattedMessage id="Feedback" />
                                </Label>
                                <TextArea
                                    className="block w-full"
                                    rows={5}
                                    name="feedback"
                                    placeholder={intl.formatMessage({
                                        id: "Provide information as to why you are reporting this opportunity",
                                    })}
                                    value={data.feedback}
                                    onChange={(e) =>
                                        setData("feedback", e.target.value)
                                    }
                                />
                                <Error error={errors.feedback} />
                            </Field>
                            {submitted && (
                                <AlertWithAccentBorder
                                    type="success"
                                    className="mt-6"
                                >
                                    <FormattedMessage id="Thank you, your feedback has been submitted for review." />
                                </AlertWithAccentBorder>
                            )}
                            <div className="flex items-center justify-end mt-6">
                                {submitted ? (
                                    <Button
                                        color="white"
                                        onClick={() => setOpen(false)}
                                    >
                                        <FormattedMessage id="Close" />
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            color="white"
                                            onClick={() => setOpen(false)}
                                        >
                                            <FormattedMessage id="Cancel" />
                                        </Button>
                                        <Button
                                            type="submit"
                                            color="black"
                                            className="ml-3"
                                        >
                                            <FormattedMessage id="Submit" />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </Dialog>
        </div>
    );
}
